import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Users, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import api, { fmt, errText, undoToast } from "@/lib/api";

const EMPTY = { name: "", phone: "", address: "", notes: "" };

export default function Customers() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const { data: customers, isLoading } = useQuery({
    queryKey: ["customers"],
    queryFn: async () => (await api.get("/customers")).data,
  });

  const create = useMutation({
    mutationFn: async () => (await api.post("/customers", form)).data,
    onSuccess: () => {
      toast.success("Customer added");
      qc.invalidateQueries({ queryKey: ["customers"] });
      setOpen(false);
      setForm(EMPTY);
    },
    onError: (e) => toast.error(errText(e)),
  });

  const update = useMutation({
    mutationFn: async () => (await api.put(`/customers/${editing}`, form)).data,
    onSuccess: () => {
      toast.success("Customer updated");
      qc.invalidateQueries({ queryKey: ["customers"] });
      qc.invalidateQueries({ queryKey: ["orders"] });
      setOpen(false);
      setEditing(null);
      setForm(EMPTY);
    },
    onError: (e) => toast.error(errText(e)),
  });

  const remove = useMutation({
    mutationFn: async (cid) => (await api.delete(`/customers/${cid}`)).data,
    onSuccess: (data) => {
      undoToast("Customer deleted", data.undo_id, () => qc.invalidateQueries({ queryKey: ["customers"] }));
      qc.invalidateQueries({ queryKey: ["customers"] });
    },
    onError: (e) => toast.error(errText(e)),
  });

  const openEdit = (e, c) => {
    e.stopPropagation();
    setEditing(c.id);
    setForm({ name: c.name || "", phone: c.phone || "", address: c.address || "", notes: c.notes || "" });
    setOpen(true);
  };

  const onDelete = (e, c) => {
    e.stopPropagation();
    if (window.confirm(`Delete customer "${c.name}"? This cannot be undone.`)) {
      remove.mutate(c.id);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="space-y-6" data-testid="customers-page">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl lg:text-3xl font-semibold tracking-tight">Customers</h1>
          <p className="text-sm text-muted-foreground mt-1">{customers?.length || 0} customers</p>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); setForm(EMPTY); } }}>
          <DialogTrigger asChild>
            <Button data-testid="add-customer-button" className="rounded-full active:scale-95 transition-transform">
              <Plus className="h-4 w-4 mr-1.5" /> Add Customer
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit Customer" : "New Customer"}</DialogTitle></DialogHeader>
            <div className="space-y-4 pt-2">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Name *</Label>
                <Input data-testid="customer-name-input" value={form.name} onChange={set("name")} placeholder="Customer name" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Phone</Label>
                <Input data-testid="customer-phone-input" value={form.phone} onChange={set("phone")} placeholder="+91..." />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Address</Label>
                <Input data-testid="customer-address-input" value={form.address} onChange={set("address")} placeholder="Address" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Notes</Label>
                <Textarea data-testid="customer-notes-input" value={form.notes} onChange={set("notes")} placeholder="Optional notes" rows={2} />
              </div>
              <Button
                data-testid="customer-save-button"
                className="w-full rounded-lg active:scale-95 transition-transform"
                disabled={!form.name.trim() || create.isPending || update.isPending}
                onClick={() => (editing ? update.mutate() : create.mutate())}
              >
                {editing ? "Save Changes" : "Save Customer"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {isLoading ? (
        <div className="space-y-2">{Array.from({ length: 5 }).map((_, i) => <div key={i} className="h-14 rounded-xl bg-muted animate-pulse" />)}</div>
      ) : customers?.length === 0 ? (
        <div className="rounded-xl border bg-card p-12 text-center">
          <Users className="h-10 w-10 mx-auto text-muted-foreground/50" />
          <p className="mt-3 font-medium">No customers yet</p>
          <p className="text-sm text-muted-foreground">Add your first customer to start creating orders.</p>
        </div>
      ) : (
        <div className="rounded-xl border bg-card overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>Name</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead className="text-right">Orders</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="text-right">Pending</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {customers.map((c) => (
                <TableRow
                  key={c.id}
                  data-testid={`customer-row-${c.id}`}
                  className="cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => navigate(`/customers/${c.id}`)}
                >
                  <TableCell className="font-medium">{c.name}</TableCell>
                  <TableCell className="text-muted-foreground">{c.phone || "—"}</TableCell>
                  <TableCell className="text-right">{c.orders_count}</TableCell>
                  <TableCell className="text-right">{fmt(c.total_amount)}</TableCell>
                  <TableCell className={`text-right font-medium ${c.pending_amount > 0 ? "text-red-500" : "text-green-600"}`}>
                    {fmt(c.pending_amount)}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`edit-customer-${c.id}`} title="Edit" onClick={(e) => openEdit(e, c)}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-red-500" data-testid={`delete-customer-${c.id}`} title="Delete" onClick={(e) => onDelete(e, c)}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
